import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { http, apiError } from "../../lib/api";

export const InquiryForm = ({ asset, division = "", onDone, showTopic = false, testid = "inquiry-form" }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", topic: "general", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/inquiries", { ...form, asset_id: asset?.id || null, division: asset?.division || division });
      setSent(true);
      toast.success("Inquiry received. We'll be in touch shortly.");
      onDone?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent)
    return (
      <div className="py-10 text-center space-y-4" data-testid={`${testid}-success`}>
        <CheckCircle2 className="w-10 h-10 text-gold mx-auto" />
        <p className="font-display text-3xl text-slate-50">Thank you.</p>
        <p className="text-sm text-slate-300 max-w-sm mx-auto">Your inquiry has been received and a confirmation has been sent to your email. Selby Asset Group will respond using the contact details you provided.</p>
      </div>
    );

  return (
    <form onSubmit={submit} className="space-y-5" data-testid={testid}>
      {asset && (
        <div className="border hairline p-4 bg-white/[0.03]">
          <p className="eyebrow !text-[0.62rem]">Asset of interest</p>
          <p className="text-slate-100 mt-1 text-sm">{asset.title}</p>
          <p className="font-mono text-xs text-slate-400 mt-1">Stock # {asset.stock_number}</p>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="label" htmlFor={`${testid}-name`}>Name *</label>
          <input id={`${testid}-name`} required className="field" value={form.name} onChange={set("name")} data-testid={`${testid}-name-input`} />
        </div>
        <div>
          <label className="label" htmlFor={`${testid}-email`}>Email *</label>
          <input id={`${testid}-email`} type="email" required className="field" value={form.email} onChange={set("email")} data-testid={`${testid}-email-input`} />
        </div>
      </div>
      <div>
        <label className="label" htmlFor={`${testid}-phone`}>Phone (optional)</label>
        <input id={`${testid}-phone`} className="field" value={form.phone} onChange={set("phone")} data-testid={`${testid}-phone-input`} />
      </div>
      {showTopic && (
        <div>
          <label className="label" htmlFor={`${testid}-topic`}>Topic</label>
          <select id={`${testid}-topic`} className="field" value={form.topic} onChange={set("topic")} data-testid={`${testid}-topic-select`}>
            <option value="general">General question / customer relations</option>
            <option value="sales">Sales: buying, availability or pricing</option>
          </select>
        </div>
      )}
      <div>
        <label className="label" htmlFor={`${testid}-msg`}>Message</label>
        <textarea id={`${testid}-msg`} rows={4} className="field resize-none" value={form.message} onChange={set("message")}
          placeholder={asset ? "Questions about availability, condition, delivery or pricing…" : "How can we help?"} data-testid={`${testid}-message-input`} />
      </div>
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={form.website} onChange={set("website")} aria-hidden="true" />
      <button type="submit" disabled={busy} className="btn-gold w-full" data-testid={`${testid}-submit`}>
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send Inquiry
      </button>
      <p className="text-xs text-slate-400">Your details are used only to respond to this inquiry and are never sold. See our <a href="/privacy" className="underline hover:text-gold">Privacy Policy</a>.</p>
    </form>
  );
};
